import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { SessionProvider } from "../app/session/SessionContext";
import type { Session } from "../app/session/session";
import type { AuthClient } from "./auth-client";
import { sessionFromToken } from "./session-mapping";

const AuthClientContext = createContext<AuthClient | null>(null);

/** Sign-in actions for pages (login, sign-out). Missing provider is a wiring bug. */
export function useAuthClient(): AuthClient {
  const client = useContext(AuthClientContext);
  if (!client) throw new Error("useAuthClient() called outside <AuthSessionProvider>");
  return client;
}

/** For shared chrome (e.g. the header) that also renders in UI tests without sign-in wiring. */
export function useOptionalAuthClient(): AuthClient | null {
  return useContext(AuthClientContext);
}

/**
 * Feeds the live Firebase session into SessionProvider (AUTH-004). Starts as "loading" so guards
 * wait instead of flashing a redirect to sign-in on every page load.
 */
export function AuthSessionProvider({
  client,
  children,
}: {
  client: AuthClient;
  children: ReactNode;
}) {
  const [session, setSession] = useState<Session>({ status: "loading" });

  useEffect(
    () =>
      client.subscribe((token) => {
        setSession(sessionFromToken(token));
      }),
    [client],
  );

  return (
    <AuthClientContext value={client}>
      <SessionProvider session={session}>{children}</SessionProvider>
    </AuthClientContext>
  );
}
